import React, { useState, useEffect, useRef } from "react";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import ArrowDownIcon from "@styles/all-images/icons/chevron-down.svg";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormikProps } from "formik";
import Tooltip from "@mui/material/Tooltip";
import { Autocomplete, TextField } from "@mui/material";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface Role {
  id: number;
  name: string;
}

interface StaffAddProps {
  onClose: () => void;
  onSubmit: () => void;
  isStaffModalOpen: boolean;
  editData?: any; // For edit mode
}

interface FormValues {
  name: string;
  roles: number[];
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Profile name is required")
    .max(50, "Profile name must be less than 50 characters"),
  roles: Yup.array().min(
    1,
    "At least one role must be assigned to the profile.",
  ),
});

const StaffAdd: React.FC<StaffAddProps> = ({
  onClose,
  onSubmit,
  isStaffModalOpen,
  editData,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [isRolesOpen, setIsRolesOpen] = useState(false);
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const [isRoleListLoading, setIsRoleListLoading] = useState(false);
  const initialValues: FormValues = {
    name: editData?.name || "",
    roles: editData?.roles.map((role: Role) => role.id) || [],
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (editData) {
      formikRef.current?.setValues({
        name: editData.name,
        roles: editData.roles.map((role: Role) => role.id),
      });
    }
  }, [editData]);

  const fetchRoles = async () => {
    try {
      setIsRoleListLoading(true);
      const { authenticatedCall } = actionsHandler("roleSelect");
      const response = await dispatch(authenticatedCall({ method: "get" }));
      if (response.ok) {
        setAvailableRoles(
          response.data.map((role: Role) => ({
            id: role.id,
            name: role.name,
          })),
        );
      }
    } catch (error) {
      console.error("Failed to fetch roles");
    } finally {
      setIsRoleListLoading(false);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      const { authenticatedCall } = actionsHandler(
        editData ? "permissionProfileById" : "permissionProfile",
      );

      const payload = {
        name: values.name,
        roles: values.roles,
      };

      const response = await dispatch(
        authenticatedCall({
          method: editData ? "put" : "post",
          payload,
          ...(editData && { customParams: [editData.id + "/"] }),
        }),
      );

      if (response.ok) {
        toast.success(
          `Profile ${editData ? "updated" : "created"} successfully`,
        );
        onSubmit();
        onClose();
      }
    } catch (error) {
      toast.error(`Failed to ${editData ? "update" : "create"} profile`);
    }
  };

  return (
    <div
      className={`tw-w-[350px] tw-fixed tw-bg-black/25 tw-top-0 tw-min-h-screen tw-flex tw-items-center tw-justify-end tw-z-50 tw-transition-all tw-ease-in-out tw-duration-300 ${
        isStaffModalOpen ? "tw-right-0" : "tw-right-[-350px]"
      }`}
    >
      <div className="tw-bg-white tw-shadow-lg tw-mx-auto tw-h-screen tw-w-[350px]">
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-border-b">
          <h2 className="tw-text-base tw-font-semibold">
            {editData ? "Edit Profile" : "Add Profile"}
          </h2>
          <button onClick={onClose}>
            <img src={CloseIcon} alt="Close" className="tw-w-auto tw-h-auto" />
          </button>
        </div>

        <div className="tw-flex tw-flex-col tw-h-[calc(100%-40px)]">
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className="tw-flex tw-flex-col tw-h-full">
                <div className="tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-4 tw-space-y-6">
                  <div className="form-group">
                    <label
                      htmlFor="profileName"
                      className="tw-block tw-text-sm tw-text-[#414651] tw-font-medium tw-mb-2"
                    >
                      Profile Name <span className="tw-text-[#B54708]">*</span>
                    </label>
                    <TextField
                      name="name"
                      type="text"
                      value={values.name}
                      onChange={(e) => setFieldValue("name", e.target.value)}
                      placeholder="Enter profile name"
                      className="tw-w-full tw-h-11 tw-px-3 tw-border tw-border-[#D5D7DA] tw-text-[#717680] tw-font-normal tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-blue-500"
                    />
                    {touched.name && errors.name && (
                      <div className="tw-text-red-500 tw-text-sm">
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <DynamicAutocomplete
                      name="roles"
                      label="Roles"
                      options={availableRoles}
                      selectedValue={values.roles}
                      labelKey="name"
                      valueKey="id"
                      onChange={(newValue: any) => {
                        setFieldValue("roles", newValue);
                      }}
                      multiple
                      maxChips={2}
                      maxLengthChip={6}
                      placeholder="Select roles"
                      filterOptions={(options, { inputValue }) =>
                        options.filter((option) =>
                          option.label
                            ?.toLowerCase()
                            .includes(inputValue?.toLowerCase() || ""),
                        )
                      }
                      error={
                        touched.roles
                          ? Array.isArray(errors.roles)
                            ? errors.roles?.join(", ")
                            : errors.roles
                          : undefined
                      }
                      touched={Boolean(touched.roles)}
                      loading={isRoleListLoading} // Add this state for loading
                    />
                  </div>
                </div>

                <div className="tw-px-4 tw-py-4 tw-bg-white">
                  <div className="tw-flex tw-space-x-4">
                    <button
                      type="submit"
                      className="tw-flex-1 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default StaffAdd;
